import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiFileText, FiZap, FiPlus, FiCheck, FiTrash2, FiChevronDown, FiChevronUp } = FiIcons;

// ─── All available merge fields grouped by source ────────────────────
const FIELD_GROUPS = [
  {
    group: 'Tenant Info',
    fields: [
      { label: 'Tenant Name', value: '[Tenant Name]' },
      { label: 'Tenant Email', value: '[Tenant Email]' },
      { label: 'Tenant Phone', value: '[Tenant Phone]' },
      { label: 'Tenant Employer', value: '[Tenant Employer]' },
      { label: 'Tenant Position', value: '[Tenant Position]' },
      { label: 'Emergency Contact', value: '[Emergency Contact]' },
    ]
  },
  {
    group: 'Property Info',
    fields: [
      { label: 'Property Name', value: '[Property Name]' },
      { label: 'Property Address', value: '[Property Address]' },
      { label: 'Lock Code', value: '[Lock Code]' },
      { label: 'Rent Rate', value: '[Rent Rate]' },
      { label: 'Property Status', value: '[Property Status]' },
    ]
  },
  {
    group: 'Lease Details',
    fields: [
      { label: 'Lease Start Date', value: '[Lease Start Date]' },
      { label: 'Lease End Date', value: '[Lease End Date]' },
      { label: 'Rent Amount', value: '[Rent Amount]' },
      { label: 'Deposit Amount', value: '[Deposit Amount]' },
      { label: 'Deposit Status', value: '[Deposit Status]' },
      { label: 'Lease Status', value: '[Lease Status]' },
    ]
  },
  {
    group: 'Dates & Signatures',
    fields: [
      { label: 'Current Date', value: '[Current Date]' },
      { label: 'Current Year', value: '[Current Year]' },
      { label: 'Signature Line', value: '[__________________]' },
      { label: 'Initials Line', value: '[____]' },
      { label: 'Manager Name', value: '[Manager Name]' },
      { label: 'Company Name', value: '[Company Name]' },
    ]
  },
];

const DOC_TYPES = [
  { id: 'email', label: 'Email', icon: '✉️' },
  { id: 'lease', label: 'Lease Agreement', icon: '📋' },
  { id: 'letter', label: 'Letter / Notice', icon: '📄' },
  { id: 'move-in', label: 'Move-In Form', icon: '🏠' },
  { id: 'move-out', label: 'Move-Out Form', icon: '📦' },
  { id: 'inspection', label: 'Inspection Report', icon: '🔍' },
  { id: 'receipt', label: 'Receipt / Invoice', icon: '🧾' },
  { id: 'other', label: 'Other', icon: '📝' },
];

const CATEGORIES = ['General', 'Onboarding', 'Lease', 'Maintenance', 'Financial', 'Legal', 'Hospitality', 'Notice'];

const TemplateModal = ({ isOpen, onClose, onSave, initialData, customFieldDefs, onAddCustomField, onRemoveCustomField }) => {
  const defaults = { name: '', title: '', subject: '', content: '', category: 'General', docType: 'email', customFields: [] };
  const [formData, setFormData] = useState(defaults);
  const customFields = (customFieldDefs || []).map(cf => ({ label: cf.name, value: `[${cf.name}]`, appliesTo: cf.appliesTo }));
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldApplies, setNewFieldApplies] = useState('tenant');
  const [showAddField, setShowAddField] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ 'Tenant Info': true, 'Property Info': true });
  const contentRef = useRef(null);

  useEffect(() => {
    if (initialData) {
      setFormData({ ...defaults, ...initialData, name: initialData.name || initialData.title || '' });
    } else {
      setFormData(defaults);
    }
    setExpandedGroups({ 'Tenant Info': true, 'Property Info': true });
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const toggleGroup = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  const allBuiltinFields = FIELD_GROUPS.flatMap(g => g.fields);
  const allFields = [...allBuiltinFields, ...customFields];
  const usedFields = allFields.filter(f => formData.content?.includes(f.value) || formData.subject?.includes(f.value));

  const injectField = (fieldValue) => {
    const textarea = contentRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content || '';
      const newText = text.substring(0, start) + fieldValue + text.substring(end);
      setFormData(prev => ({ ...prev, content: newText }));
      setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + fieldValue.length, start + fieldValue.length); }, 0);
    } else {
      setFormData(prev => ({ ...prev, content: (prev.content || '') + ' ' + fieldValue }));
    }
  };

  const addCustomField = () => {
    const name = newFieldName.trim();
    if (!name) return;
    const value = `[${name}]`;
    if (allFields.some(f => f.value === value)) return;
    if (onAddCustomField) onAddCustomField({ name, appliesTo: newFieldApplies });
    setNewFieldName('');
    setNewFieldApplies('tenant');
    setShowAddField(false);
  };

  const removeCustomField = (value) => {
    const fieldName = value.replace(/^\[|\]$/g, '');
    if (onRemoveCustomField) onRemoveCustomField(fieldName);
  };

  const handleSave = () => {
    const saveData = {
      ...formData,
      title: formData.name,
      lastUpdated: new Date().toISOString().split('T')[0],
      customFields: customFields.filter(f => (formData.content || '').includes(f.value) || (formData.subject || '').includes(f.value))
    };
    onSave(saveData);
    onClose();
  };

  const selectedDocType = DOC_TYPES.find(d => d.id === formData.docType) || DOC_TYPES[0];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col md:flex-row">

        {/* Sidebar — Merge Fields */}
        <div className="w-full md:w-56 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col shrink-0 max-h-[35vh] md:max-h-none">
          <div className="p-3 border-b border-gray-100">
            <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
              <SafeIcon icon={FiZap} className="text-amber-500" size={12} /> Merge Fields
            </h3>
            <p className="text-[9px] text-gray-400 mt-0.5">Click to insert at cursor</p>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {FIELD_GROUPS.map(g => (
              <div key={g.group}>
                <button onClick={() => toggleGroup(g.group)}
                  className="w-full flex items-center justify-between px-2 py-1.5 text-[9px] font-semibold text-gray-400 uppercase hover:text-gray-600 transition-colors">
                  {g.group}
                  <SafeIcon icon={expandedGroups[g.group] ? FiChevronUp : FiChevronDown} size={10} />
                </button>
                {expandedGroups[g.group] && (
                  <div className="space-y-0.5 mb-1">
                    {g.fields.map(f => (
                      <button key={f.value} type="button" onClick={() => injectField(f.value)}
                        className={`w-full text-left px-2.5 py-1 rounded-md text-[11px] transition-colors ${
                          usedFields.some(u => u.value === f.value) ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-500 hover:bg-white hover:text-indigo-600'
                        }`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {customFields.length > 0 && (
              <div>
                <p className="px-2 py-1.5 text-[9px] font-semibold text-gray-400 uppercase">Custom</p>
                {customFields.map(f => (
                  <div key={f.value} className="flex items-center gap-0.5">
                    <button type="button" onClick={() => injectField(f.value)}
                      className={`flex-1 text-left px-2.5 py-1 rounded-md text-[11px] transition-colors ${
                        usedFields.some(u => u.value === f.value) ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-500 hover:bg-white hover:text-purple-600'
                      }`}>
                      {f.label}
                    </button>
                    <button type="button" onClick={() => removeCustomField(f.value)} className="p-0.5 text-gray-300 hover:text-red-500">
                      <SafeIcon icon={FiTrash2} size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-2 border-t border-gray-100">
            {showAddField ? (
              <div className="space-y-1.5">
                <input autoFocus value={newFieldName} onChange={e => setNewFieldName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomField(); } if (e.key === 'Escape') { setShowAddField(false); setNewFieldName(''); } }}
                  className="w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs outline-none focus:border-indigo-500" placeholder="Field name..." />
                <select value={newFieldApplies} onChange={e => setNewFieldApplies(e.target.value)}
                  className="w-full px-2 py-1 rounded-md border border-gray-200 text-[10px] outline-none">
                  <option value="tenant">Tenant field</option>
                  <option value="property">Property field</option>
                  <option value="both">Both</option>
                </select>
                <div className="flex gap-1">
                  <button type="button" onClick={addCustomField} className="flex-1 py-1 bg-indigo-600 text-white rounded-md text-[10px] font-semibold">Add</button>
                  <button type="button" onClick={() => { setShowAddField(false); setNewFieldName(''); }} className="flex-1 py-1 bg-gray-100 text-gray-500 rounded-md text-[10px]">Cancel</button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setShowAddField(true)}
                className="w-full flex items-center justify-center gap-1 py-1.5 border border-dashed border-gray-200 rounded-md text-[10px] text-gray-400 hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                <SafeIcon icon={FiPlus} size={11} /> Add Custom Field
              </button>
            )}
          </div>
        </div>

        {/* Main Form */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xl">{selectedDocType.icon}</span>
              <div>
                <h2 className="text-base font-bold text-gray-900">{initialData?.id ? 'Edit Template' : 'Create Template'}</h2>
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest">{selectedDocType.label}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <SafeIcon icon={FiX} size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Document Type */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Document Type</label>
              <div className="flex flex-wrap gap-1.5">
                {DOC_TYPES.map(dt => (
                  <button key={dt.id} type="button" onClick={() => setFormData(prev => ({ ...prev, docType: dt.id }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                      formData.docType === dt.id ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-gray-100 text-gray-500 hover:border-gray-200'
                    }`}>
                    <span className="text-sm">{dt.icon}</span> {dt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name & Category */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Template Name *</label>
                <input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" placeholder="e.g. Standard Lease Agreement" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
                <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Subject line (for emails/letters) */}
            {['email', 'letter', 'other'].includes(formData.docType) && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Subject Line</label>
                <input value={formData.subject} onChange={e => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" placeholder="e.g. Welcome to [Property Name]" />
              </div>
            )}

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">Content *</label>
                {usedFields.length > 0 && (
                  <span className="text-[10px] text-indigo-600 font-semibold">{usedFields.length} merge field{usedFields.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <textarea ref={contentRef} required rows={12} value={formData.content}
                onChange={e => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-none font-mono leading-relaxed"
                placeholder="Write your template content here. Click merge fields on the left to insert dynamic placeholders that auto-fill with tenant, property, and lease data when generating documents." />
            </div>

            {/* Used fields preview */}
            {usedFields.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Merge fields in this template</p>
                <div className="flex flex-wrap gap-1">
                  {usedFields.map(f => (
                    <span key={f.value} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">{f.label}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleSave}
              className="px-5 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 text-sm">
              <SafeIcon icon={FiCheck} size={14} /> {initialData?.id ? 'Update' : 'Save Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
